import {
    createEntityAttribute,
    downloadEntityAttributeValues,
    getEntityAttributes,
    uploadEntityAttributeValues,
    type EntityAttributeDefinition,
} from '@api/entity-attributes';
import useToaster from '@hooks/toaster/useToaster';
import {
    Alert,
    Anchor,
    Button,
    FileInput,
    Group,
    Modal,
    Stack,
    Text,
} from '@mantine/core';
import { useCallback, useEffect, useState, type FC } from 'react';

/**
 * Per-SKU reorder DRR windows in bulk.
 *
 * WHAT THIS USED TO BE. The same modal uploaded absolute reorder POINTS — "this SKU triggers at
 * 5,000 units" — which overrode the formula outright. That has been removed: a fixed quantity
 * has no run rate behind it, so it did not move when the SKU's demand did, and no column on the
 * grid could explain where it came from. Every reorder point is now derived the same way, and
 * the per-SKU exception is which DRR WINDOW the rate is read over.
 *
 * The bulk path is kept because the alternative is a dropdown per row: a brand deciding its
 * A-tier lines should track a 7-day read is making one decision about several hundred SKUs, and
 * clicking that in one at a time is not a workflow.
 *
 * Stored as a STATIC entity attribute on PRODUCT — no new table, and the values also show up as
 * an editable column in Entity Master. The key is passed explicitly server-side rather than
 * slugified from the name, so the label below is a safe copy-edit.
 */
const ATTRIBUTE_NAME = 'Reorder DRR Window (days)';
const ATTRIBUTE_KEY = 'rop_drr_window_days';

type Props = {
    opened: boolean;
    onClose: () => void;
    onUploaded: () => void;
};

const ReorderPointUploadModal: FC<Props> = ({ opened, onClose, onUploaded }) => {
    const { showToastSuccess, showToastError } = useToaster();
    const [definition, setDefinition] = useState<EntityAttributeDefinition | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!opened) return;
        getEntityAttributes('PRODUCT')
            .then((attrs) => {
                setDefinition(attrs.find((a) => a.key === ATTRIBUTE_KEY) ?? null);
            })
            .catch(() => setDefinition(null));
    }, [opened]);

    const handleUpload = useCallback(async () => {
        if (!file) return;
        setBusy(true);
        try {
            // First upload for a brand also creates the attribute — one less setup step
            // than asking them to define the column in Entity Master beforehand.
            const target =
                definition ??
                (await createEntityAttribute({
                    entityType: 'PRODUCT',
                    name: ATTRIBUTE_NAME,
                }));
            const result = await uploadEntityAttributeValues(target.id, file);
            setDefinition(target);
            showToastSuccess({
                title: `${result.uploaded} reorder window(s) saved`,
                subtitle: result.skipped
                    ? `${result.skipped} row(s) skipped — SKU not recognised or value not a number`
                    : undefined,
            });
            setFile(null);
            onUploaded();
            onClose();
        } catch (error) {
            showToastError({
                title: 'Could not upload reorder windows',
                subtitle: error instanceof Error ? error.message : undefined,
            });
        } finally {
            setBusy(false);
        }
    }, [file, definition, showToastSuccess, showToastError, onUploaded, onClose]);

    return (
        <Modal opened={opened} onClose={onClose} title="Upload reorder windows" size="md">
            <Stack gap="md">
                <Text size="sm" c="dimmed">
                    A CSV of two columns — the product ID and the DRR window in days (7, 14,
                    30…). Products you leave out keep inheriting their tier rule, then the
                    merchant default.
                </Text>

                <Alert variant="light" color="blue">
                    <Text size="xs">
                        This sets what a SKU&apos;s reorder point is <b>sized on</b>, not the
                        reorder point itself — that stays{' '}
                        <b>DRR × (lead time + safety)</b> and keeps moving with demand. A short
                        window tracks a trend closely; a long one refuses to chase noise.
                    </Text>
                </Alert>

                {definition && (
                    <Anchor
                        component="button"
                        type="button"
                        size="xs"
                        onClick={() => downloadEntityAttributeValues(definition.id)}
                    >
                        Download current reorder windows
                    </Anchor>
                )}

                <FileInput
                    label="CSV file"
                    placeholder="Choose a file"
                    accept=".csv"
                    value={file}
                    onChange={setFile}
                    clearable
                />

                <Group justify="flex-end">
                    <Button variant="default" size="xs" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button size="xs" loading={busy} disabled={!file} onClick={handleUpload}>
                        Upload
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default ReorderPointUploadModal;
